import React, { useState, useRef, useEffect } from 'react';
import api from '../../services/api';

const ClientSearch = ({ token, onSelect, onAddNew, selectedClient, onClear }) => {
    const [query, setQuery]           = useState('');
    const [results, setResults]       = useState([]);
    const [loading, setLoading]       = useState(false);
    const [showDrop, setShowDrop]     = useState(false);
    const [searched, setSearched]     = useState(false);
    const dropRef                     = useRef(null);

    // Close dropdown on outside click
    useEffect(() => {
        const handler = (e) => {
            if (dropRef.current && !dropRef.current.contains(e.target)) setShowDrop(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const handleSearch = async () => {
        if (!query.trim()) return;
        setLoading(true);
        setSearched(true);
        try {
            const res = await api.get(`/clients/search?q=${encodeURIComponent(query)}`);
            setResults(res.data);
            setShowDrop(true);
        } catch {
            setResults([]);
            setShowDrop(true);
        } finally {
            setLoading(false);
        }
    };

    const handleKeyDown = (e) => { if (e.key === 'Enter') { e.preventDefault(); handleSearch(); } };

    const handleSelect = (client) => {
        onSelect(client);
        setQuery(client.name);
        setShowDrop(false);
    };

    if (selectedClient) {
        return (
            <div className="flex items-center justify-between px-2 py-1 bg-blue-50 border border-blue-200 rounded-lg">
                <div>
                    <span className="text-sm font-semibold text-blue-800">{selectedClient.name}</span>
                    {selectedClient.clientCode && (
                        <span className="block text-xs text-blue-400">{selectedClient.clientCode}</span>
                    )}
                </div>
                <button
                    type="button"
                    onClick={() => { onClear(); setQuery(''); setSearched(false); setResults([]); }}
                    className="text-xs text-red-800 hover:text-red-500 ml-2 transition-colors"
                >
                    ✕
                </button>
            </div>
        );
    }

    return (
        <div className="relative" ref={dropRef}>
            <div className="flex gap-2">
                <input
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Search client by name…"
                    className="flex-1 px-2 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition"
                />
                <button
                    type="button"
                    onClick={handleSearch}
                    disabled={loading || !query.trim()}
                    className="px-2 py-1 bg-gray-800 hover:bg-gray-700 disabled:opacity-40 text-white rounded-lg text-sm transition-colors"
                >
                    {loading ? '…' : '🔍'}
                </button>
            </div>

            {showDrop && (
                <ul className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto text-sm">
                    {results.map(c => (
                        <li
                            key={c._id}
                            onClick={() => handleSelect(c)}
                            className="px-2 py-1 hover:bg-blue-50 cursor-pointer flex justify-between items-center"
                        >
                            <span className="font-medium">{c.name}</span>
                            {c.clientCode && <span className="text-gray-400 text-xs">{c.clientCode}</span>}
                        </li>
                    ))}
                    {results.length === 0 && searched && (
                        <li className="px-3 py-3 text-gray-400 text-center text-xs">No clients found</li>
                    )}
                    <li
                        onClick={() => { setShowDrop(false); onAddNew(query); }}
                        className="px-2 py-1 border-t border-gray-100 text-blue-600 hover:bg-blue-50 cursor-pointer font-medium flex items-center gap-1"
                    >
                        <span className="text-lg leading-none">+</span> Add "{query || 'new client'}"
                    </li>
                </ul>
            )}
        </div>
    );
};

export default ClientSearch;